import * as React from 'react';
import { NavLink } from 'react-router-dom';

import { Icon } from 'elements';
import { ROUTES } from 'global/constants';
import { UserAvatar } from 'user/components/UserAvatar';
import { WrappedMessage } from 'utils';
import {
    Button,
    LabXChangeLogo,
    LanguageSwitcherModal,
} from 'ui/components';
import messages from '../displayMessages';

interface MobileSidebarProps {
    visible: boolean;
    username: string;
    notificationCount: number;
    onLogIn: () => void;
    onLogOut: () => void;
    onSignup: () => void;
    onHideSidebar: () => void;
}

interface State {
    showLanguageSwitcher: boolean;
}

export class MobileSidebar extends React.PureComponent<MobileSidebarProps, State> {
    constructor(props: MobileSidebarProps) {
        super(props);
        this.state = {
            showLanguageSwitcher: false,
        };
    }

    public async componentDidUpdate(prevProps: MobileSidebarProps) {
        // if we're transitioning from closed to open, we need to focus the first element in the sidebar for a11y
        if (!prevProps.visible && this.props.visible) {
            document.querySelector<HTMLElement>('.sidebar-container a')?.focus();
        }
    }

    public render() {
        if (!this.props.visible) {
            return null;
        }

        return (
            <nav className='sidebar-container'>
                {this.state.showLanguageSwitcher &&
                    <LanguageSwitcherModal onClose={() => this.setState({ showLanguageSwitcher: false })} />
                }
                <div className='sidebar'>
                    {this.props.username !== ''
                        ? this.renderLoggedInSidebar()
                        : this.renderLoggedOutSidebar()
                    }
                </div>
                <div role='button' className='sidebar-backdrop' tabIndex={0}
                    onClick={this.props.onHideSidebar}
                    onKeyPress={(event: React.KeyboardEvent) => {
                        if (event.key === 'Enter') {
                            this.props.onHideSidebar();
                        }
                    }}
                />
            </nav>
        );
    }

    private renderLoggedInSidebar() {
        return (
            <>
                <div className='d-flex flex-row user-welcome'>
                    <div className='left-box'>
                        <NavLink onClick={this.props.onHideSidebar} to={ROUTES.Users.PROFILE_SLUG(this.props.username)}>
                            <UserAvatar username={this.props.username} />
                        </NavLink>
                    </div>
                    <div className='flex-column right-box'>
                        <div className='welcome-msg'>
                            <WrappedMessage message={messages.uiSidebarLoginMessage} />
                        </div>
                        <div className='user-name'>
                            {this.props.username}
                        </div>
                        <NavLink onClick={this.props.onHideSidebar} to={ROUTES.Personal.PROFILE_SETTINGS} className='profile-link'>
                            <WrappedMessage message={messages.uiProfileSettings}/> <Icon name='chevron-right'/>
                        </NavLink>
                    </div>
                </div>

                <NavLink className='navlink' onClick={this.props.onHideSidebar} to={ROUTES.Explore.HOME} >
                    <Icon zoom='22' name='explore' />
                    <WrappedMessage message={messages.uiNavExplore} />
                </NavLink>
                <NavLink className='navlink' onClick={this.props.onHideSidebar} to={ROUTES.Library.HOME} >
                    <Icon zoom='22' name='library' outline />
                    <WrappedMessage message={messages.uiNavLibrary}/>
                </NavLink>

                <hr/>

                <NavLink className='navlink' onClick={this.props.onHideSidebar} to={ROUTES.Dashboard.HOME} >
                    <Icon zoom='22' name='dashboard'/>
                    <WrappedMessage message={messages.uiNavDashboard}/>
                </NavLink>

                <NavLink className='navlink' onClick={this.props.onHideSidebar} to={ROUTES.Community.HOME}>
                    <Icon zoom='22' name='organization'/>
                    <WrappedMessage message={messages.uiNavCommunity}/>
                </NavLink>
                <hr/>

                <NavLink className='navlink' onClick={this.props.onHideSidebar} to={ROUTES.Personal.NOTIFICATIONS} >
                    <Icon zoom='22' name='bell' outline />
                    <WrappedMessage message={messages.uiMyNotifications}/>
                    {this.props.notificationCount > 0 &&
                        <span className='noti-badge'>
                            {this.props.notificationCount}
                        </span>
                    }
                </NavLink>
                <NavLink className='navlink' onClick={this.props.onHideSidebar} to={ROUTES.Messages.HOME} >
                    <Icon zoom='22' name='message3' outline />
                    <WrappedMessage message={messages.uiMyMessages}/>
                </NavLink>
                <NavLink className='navlink' onClick={this.props.onHideSidebar} to={ROUTES.Personal.PROFILE_SETTINGS} >
                    <Icon zoom='22' name='settings3' />
                    <WrappedMessage message={messages.uiSettings} />
                </NavLink>

                <hr/>

                <NavLink to='#' className='navlink' onClick={this.props.onLogOut} >
                    <Icon zoom='22' name='logout' />
                    <WrappedMessage message={messages.uiLogOut} />
                </NavLink>
            </>
        );
    }

    private renderLoggedOutSidebar() {
        return <>
            <LabXChangeLogo width={120} type='trim' theme='dark' />
            <NavLink className='navlink' onClick={this.props.onHideSidebar} to={ROUTES.Explore.HOME} >
                <Icon zoom='22' name='explore' />
                <WrappedMessage message={messages.uiNavExplore} />
            </NavLink>
            <NavLink className='navlink' onClick={this.props.onHideSidebar} to={ROUTES.Library.HOME} >
                <Icon zoom='22' name='library' outline />
                <WrappedMessage message={messages.uiNavLibrary}/>
            </NavLink>

            <hr/>

            <NavLink className='navlink' onClick={this.props.onHideSidebar} to={ROUTES.General.ABOUT} >
                <Icon zoom='22' name='about'/>
                <WrappedMessage message={messages.uiAbout}/>
            </NavLink>
            <NavLink
                className='navlink'
                to='#'
                onClick={() => this.setState({ showLanguageSwitcher: true })}
            >
                <Icon zoom='22' name='language'/>
                <WrappedMessage message={messages.uiSetLanguage} />
            </NavLink>

            <hr/>

            <Button
                btnStyle='outline'
                onClick={() => {
                    this.props.onHideSidebar();
                    this.props.onLogIn();
                }}
                label={messages.uiSignIn}
            />
            <Button
                btnStyle='primary'
                onClick={() => {
                    this.props.onHideSidebar();
                    this.props.onSignup();
                }}
                label={messages.uiSignUp}
            />
        </>;
    }
}
